import { getHashedUserEmail, sendGA4Event } from "analytics/ga4-helper";
import { IAnanlyticsCompactProduct, ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { IOrderedItemView } from "interfaces/session/ShoppingCart";
import tryCatchProxy from "../modules/lib/tryCatchProxy";
import { getLSItem } from "./localStorageUtils";
const currencyCode = "CAD";

const productCategory = (product: any) => {
  if (product) {
    return `${product.tier0}/${product.tier1}/${product.tier2}/${product.tier3}`;
  }
  return undefined;
};

function getStructuredProduct(product: IAnanlyticsCompactProduct) {
  const {
    sku: id, quantity, price, description: name, manufacturer: brand, productLines,
  } = product;

  const productLine = productLines?.length ? productLines[0] : "";
  return {
    id,
    brand,
    price: price / 100,
    name,
    quantity,
    category: productCategory(product.category || product),
    productLine,
  };
}

export function getStructuredProductGA4(product: IAnanlyticsCompactProduct) {
  const { productLines } = product;

  const productLine = productLines?.length ? productLines[0] : "";
  const category = product.enCategory || product.primaryCategory;

  return {
    item_id: product.sku,
    item_name: product.description,
    item_brand: product.manufacturer,
    item_category: category.tier0,
    item_category2: category.tier1,
    item_category3: category.tier2,
    item_category4: category.tier3,
    // location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
    price: product.price / 100,
    quantity: product.quantity,
    productLine, // this field desn't exist in parameters, how to send this ?
  };
}

export function sendGA4ProductListViewEvent(products: ICompactProduct[], item_list_name: string) {
  sendGA4Event("view_item_list", {
    item_list_id: item_list_name,
    item_list_name,
    items: products.map((_) => getStructuredProductGA4(_)),
  });
}

export function sendGA4ProductViewEvent(product: ICompactProduct, item_list_name: string) {
  sendGA4Event("view_item", {
    item_list_id: item_list_name,
    item_list_name,
    currency: "CAD",
    value: product.price / 100,
    items: [
      getStructuredProductGA4(product),
    ],
  });
}

export function sendGA4ViewCartEvent(products: IOrderedItemView[], totalCost: number) {
  sendGA4Event("view_cart", {
    currency: "CAD",
    value: totalCost / 100,
    items: products.map((_) => getStructuredProductGA4(_)),
  });
}

export function sendGA4RemoveFromCartEvent(product: IAnanlyticsCompactProduct) {
  sendGA4Event("remove_from_cart", {
    currency: "CAD",
    value: (product.price / 100) * product.quantity,
    items: [
      getStructuredProductGA4(product),
    ],
  });
}

export function sendGA4AddToCartEvent(product: IAnanlyticsCompactProduct) {
  sendGA4Event("add_to_cart", {
    currency: "CAD",
    value: (product.price / 100) * product.quantity,
    items: [
      getStructuredProductGA4(product),
    ],
  });
}

export function sendGA4PurchaseEvent(orderedItems: IOrderedItemView[], {
  orderId,
  totalAmount,
}: any) {
  const revenue = totalAmount / 100;
  sendGA4Event("purchase", {
    transaction_id: orderId,
    value: revenue,
    currency: "CAD",
    // coupon: "SUMMER_SALE",
    // tax: 4.90,
    // shipping: 5.99,
    items: (orderedItems || []).map((_: any) => getStructuredProductGA4(_)),
  });
}

class EnhancedEcommerce {
  // When User first views the product in a list of search or category page

  // Called when a link to a product detail is clicked.
  static onProductClick() {

  }

  // After clicking on the product listing, a user views the product details page.
  static addProductDetailView() {

  }

  // Called when a product is added to a shopping cart.
  static async addToCart(product: IAnanlyticsCompactProduct) {
    // GTM
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({ ecommerce: null });

    const email = getLSItem("email");
    const hashedUserEmailObj = email ? await getHashedUserEmail(email) : {};

    window.dataLayer.push({
      event: "addToCart",
      ...hashedUserEmailObj,
      ecommerce: {
        currencyCode,
        add: {
          products: [getStructuredProduct(product)],
        },
      },
    });
  }

  static removeFromCart(product: IAnanlyticsCompactProduct) {
    // GTM

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({ ecommerce: null });

    window.dataLayer.push({
      event: "removeFromCart",
      ecommerce: {
        remove: {
          products: [getStructuredProduct(product)],
        },
      },
    });
  }

  // when the user completes the checkout process and submits their purchase.
  static async postCheckoutAndPurchase(orderedItems: IOrderedItemView[], {
    orderId,
    totalAmount,
    email,
  }: any) {
    const revenue = totalAmount / 100;
    // GTM

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({ ecommerce: null });

    const hashedUserEmailObj = await getHashedUserEmail(email);
    window.dataLayer.push({
      event: "purchase",
      ...hashedUserEmailObj,
      ecommerce: {
        purchase: {
          actionField: {
            id: orderId,
            revenue,
          },
          products: (orderedItems || []).map((_: any) => getStructuredProduct(_)),
        },
      },
    });
  }
}

export default tryCatchProxy(EnhancedEcommerce);
